// Unsent composer text that survives leaving the page (#1836).
//
// Someone types half a question, goes to Tables to copy a name, and comes
// back to an empty box. The text is mirrored to sessionStorage under a key the
// host names (a chat's session id, the start screen's customer): tab-scoped,
// survives navigation and reload, gone when the tab closes. It is never sent
// to the server. With no key the hook is plain useState.
//
// Storage may throw or be missing (private windows, blocked site data), so
// every access is guarded and the box works without it.

import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react'

export function composerDraftStorageKey(key: string): string {
  return `platinum:composer-draft:${key}`
}

function readDraft(key: string | undefined): string {
  if (!key) return ''
  try {
    return sessionStorage.getItem(composerDraftStorageKey(key)) ?? ''
  } catch {
    return ''
  }
}

function writeDraft(key: string | undefined, text: string): void {
  if (!key) return
  try {
    if (text) sessionStorage.setItem(composerDraftStorageKey(key), text)
    else sessionStorage.removeItem(composerDraftStorageKey(key))
  } catch {
    // Storage unavailable: the draft just does not persist.
  }
}

/** Returns [text, setText, clearDraft]. Setting '' (or clearDraft) removes the stored draft. */
export function useComposerDraft(key?: string): [string, Dispatch<SetStateAction<string>>, () => void] {
  const [text, setTextState] = useState(() => readDraft(key))
  const keyRef = useRef(key)
  const textRef = useRef(text)

  // A host that switches key without remounting (a chat switching session)
  // gets the new key's draft. Render-phase guard, not an effect: it is
  // initialisation from a changed input, with nothing to clean up.
  if (keyRef.current !== key) {
    keyRef.current = key
    const next = readDraft(key)
    textRef.current = next
    setTextState(next)
  }

  const setText: Dispatch<SetStateAction<string>> = useCallback(value => {
    const next = typeof value === 'function' ? value(textRef.current) : value
    textRef.current = next
    writeDraft(keyRef.current, next)
    setTextState(next)
  }, [])

  const clearDraft = useCallback(() => setText(''), [setText])

  return [text, setText, clearDraft]
}

export default useComposerDraft
